/**
 * What every supplier has quoted for one item, most recent first.
 *
 * ---- Why this is on the RFQ line and not only in the supplier picker ----
 *
 * `SupplierPickerModal` already reads the same history, but it shows one number per supplier —
 * their last known price — because its job is choosing who to ask. The question this answers is
 * different: *what has this item actually cost, over time, across everyone?* That is what tells a
 * purchaser whether a quote coming back is normal or not, and it is worth seeing before the RFQ
 * goes out rather than after.
 *
 * Read-only, and reachable from the line whether or not the item is matched to suppliers yet.
 */

import { useState } from 'react'
import { Modal } from '../../components/Modal'
import { Button, Placeholder } from '../../components/ui'
import { Money } from '../../components/Money'
import { useItemQuoteHistory } from '../../lib/api/useItemQuoteHistory'
import { formatDate, formatMoney } from '../../lib/format'
import { priceFromHistory, priceFromStock } from '../../lib/directPricing'
import { describeSourcing, summariseSourcing } from '../../lib/sourcingSummary'
import type { ItemContract } from '../../lib/types'
import type { EnquiryItemPatch, Money as MoneyValue, PurchaseHistoryEntry } from '../../lib/types'

/**
 * Per supplier: their most recent price, and how it moved since the one before it.
 *
 * The comparison is a supplier against THEMSELVES. Comparing across suppliers would be the wrong
 * arithmetic — two suppliers quoting different prices is a market, not a price rise.
 */
interface SupplierTrend {
  supplier: string
  latest: PurchaseHistoryEntry
  /** The same supplier's previous quote, when they have one. */
  previous?: PurchaseHistoryEntry
}

export function summariseBySupplier(entries: PurchaseHistoryEntry[]): SupplierTrend[] {
  const bySupplier = new Map<string, PurchaseHistoryEntry[]>()
  for (const entry of entries) {
    const list = bySupplier.get(entry.supplier) ?? []
    list.push(entry)
    bySupplier.set(entry.supplier, list)
  }
  return [...bySupplier.entries()]
    .map(([supplier, rows]) => {
      const sorted = [...rows].sort((a, b) => (a.date < b.date ? 1 : -1))
      return { supplier, latest: sorted[0], previous: sorted[1] }
    })
    // Whoever quoted most recently first — the most likely to quote again.
    .sort((a, b) => (a.latest.date < b.latest.date ? 1 : -1))
}

/** `null` when there is nothing to compare, so the UI shows nothing rather than "0%". */
export function priceChangePct(trend: SupplierTrend): number | null {
  const before = trend.previous?.price.amount
  const now = trend.latest.price.amount
  if (before === undefined || before <= 0 || now <= 0) return null
  // Different currencies cannot be compared without an FX rate this modal does not have.
  if (trend.previous?.price.currency !== trend.latest.price.currency) return null
  return Math.round(((now - before) / before) * 100)
}

export function ItemHistoryModal({
  itemCode,
  itemName,
  uom,
  available,
  ourPrice,
  contracts = [],
  rfqSuppliers = [],
  onPrice,
  onClose,
}: {
  itemCode: string
  itemName: string
  /** The line's own unit, so a rate is never shown against an unstated one. */
  uom: string
  /** What can actually be sold from our shelf — `undefined` when nothing has answered. */
  available?: number
  /** The catalogue's price for this item, when it has one. */
  ourPrice?: MoneyValue | null
  /** Standing contracts covering this item, from the selected blanket orders. */
  contracts?: ItemContract[]
  /** Suppliers this line's RFQ would go to. */
  rfqSuppliers?: string[]
  /**
   * Price this line from the chosen source, skipping the RFQ entirely.
   *
   * `undefined` makes the dialog read-only — which is what it was, and what it stays anywhere the
   * caller cannot write (no line in context).
   */
  onPrice?: (patch: Pick<EnquiryItemPatch, 'route' | 'selected_rate' | 'suppliers'>, label: string) => void
  onClose: () => void
}) {
  /*
    Only for an item the catalogue has NO price for — the one case where nothing can be quoted
    without asking. Where a price exists it is used directly and there is nothing to type: the
    catalogue figure is already our selling price.
  */
  const [ownCost, setOwnCost] = useState('')
  /*
    Read from `Supplier Quotation Item` rather than `items.get_purchase_history`, which answers
    `[]` for every item on this bench — see `useItemQuoteHistory`. Same shape either way, so
    everything below is unchanged.
  */
  const history = useItemQuoteHistory(itemCode)
  const trends = summariseBySupplier(history.data ?? [])

  return (
    <Modal title={`Quote history — ${itemName || itemCode}`} onClose={onClose}>
      {/*
        Every way this item can be bought, on one line.

        The four sources live on four different screens — contracts on the bar above the table,
        past quotes here, stock in its own column, RFQ suppliers in another. Nothing showed them
        together, so "send it to RFQ" was the default even for items already on a contract or
        sitting in the warehouse. This is the sentence that makes the alternatives visible before
        that decision is taken.
      */}
      <div className="pad" style={{ borderBottom: '1px solid var(--line)', fontWeight: 600 }}>
        {describeSourcing(
          summariseSourcing({ contracts, history: history.data ?? [], available, rfqSuppliers }),
          uom,
        )}
      </div>

      <div className="pad" style={{ color: 'var(--muted)', fontSize: 12.5 }}>
        Every price quoted for <b>{itemCode}</b>, newest first. The arrow compares each
        supplier with their <i>own</i> previous quote — two suppliers differing is a market, not a rise.
      </div>

      {/*
        Supply it ourselves instead.

        Offered only when there is stock TO supply from: `available` is shelf minus reserved, so a
        line already promised elsewhere does not present itself as available. Like the history
        option this skips the RFQ — but there is no supplier, so the quotation names it as
        in-house rather than leaving the source blank.
      */}
      {onPrice ? (
        <div className="pad" style={{ borderBottom: '1px solid var(--line)', background: 'var(--bg)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Supply from our own stock —{' '}
            {/*
              ALWAYS shown, with the availability stated — it used to be hidden whenever stock was
              zero or unknown, which is most items, so the option looked as though it did not
              exist. Hiding a control teaches nobody it is there; disabling it and saying why does.
            */}
            {available === undefined ? (
              <span style={{ color: 'var(--muted)' }}>stock not known for this item</span>
            ) : available > 0 ? (
              <span style={{ color: 'var(--ok)' }}>{available} {uom} available</span>
            ) : (
              <span style={{ color: 'var(--danger)' }}>none available ({available} {uom})</span>
            )}
          </div>
          {/*
            ONE CLICK, no typing.

            CORRECTED 2026-09-03 (Akhil): the catalogue figure is a COST, not a selling price. This
            block used to say the opposite and quote it as-is with no margin, which sold every
            in-house line at cost. The selling price is decided on the customer quotation page,
            where the margin rules are applied — so this supplies the COST and stops there, exactly
            like picking a supplier's past rate does.

            The typed field survives only for an item the catalogue has no price for, where there
            genuinely is nothing to quote from.
          */}
          {/*
            NOT gated on availability (Akhil, 2026-09-07). These used to be disabled unless
            `available > 0`, so an item showing `-10 PKT` could not be priced from our own cost at
            all — and negative availability is over-COMMITMENT, not absence: the goods may be on
            order, or the reservation may be the very one being fulfilled.

            What this supplies is a COST, and taking it commits no stock. Whether we can actually
            supply is a judgement the purchaser makes with the figure stated directly above;
            refusing the click made that judgement for them and left no way to price the line.
          */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {ourPrice ? (
              <Button
                variant="primary"
                title={
                  available !== undefined && available > 0
                    ? `Supply this line from our own stock at a cost of ${formatMoney(ourPrice)} — no RFQ. The selling price and margin are set on the quotation.`
                    : `Price this line at our own cost of ${formatMoney(ourPrice)}. Nothing is free to commit right now — check you can supply it.`
                }
                onClick={() => {
                  const patch = priceFromStock(ourPrice.amount)
                  if (patch) onPrice(patch, `our own stock at ${formatMoney(ourPrice)}/${uom}`)
                }}
              >
                Use our stock @ {formatMoney(ourPrice)}/{uom}
              </Button>
            ) : (
              <>
                <label className="ef" style={{ width: 150 }}>
                  <span>Our cost</span>
                  <input
                    inputMode="decimal"
                    value={ownCost}
                    placeholder="—"
                    onChange={(e) => setOwnCost(e.target.value)}
                  />
                </label>
                <Button
                  variant="primary"
                  // Only the COST is required — availability does not gate this either; see above.
                  disabled={!(Number(ownCost) > 0)}
                  onClick={() => {
                    const patch = priceFromStock(Number(ownCost))
                    if (patch) onPrice(patch, `our own stock at ${ownCost}`)
                  }}
                >
                  Use our stock
                </Button>
              </>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
            {ourPrice
              ? 'This is the COST. The selling price and margin are set on the customer quotation.'
              : 'The catalogue has no cost for this item — enter what it costs us.'}
          </div>
        </div>
      ) : null}

      {history.isPending ? <Placeholder>Loading history…</Placeholder> : null}
      {history.error ? (
        <Placeholder>Could not load the history: {(history.error as Error).message}</Placeholder>
      ) : null}
      {!history.isPending && !history.error && trends.length === 0 ? (
        <div className="pad" style={{ color: 'var(--muted)' }}>
          Nobody has quoted this item yet — nothing to compare a new quote against.
        </div>
      ) : null}

      {trends.map((trend) => {
        const change = priceChangePct(trend)
        return (
          <div key={trend.supplier} className="srow" style={{ display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span className="nm">{trend.supplier}</span>
              <span style={{ whiteSpace: 'nowrap' }}>
                <b>
                  <Money value={trend.latest.price} />
                </b>
                <span style={{ color: 'var(--muted)' }}>/{trend.latest.uom}</span>
                {change !== null ? (
                  <span
                    style={{ marginLeft: 8, color: change > 0 ? 'var(--danger)' : change < 0 ? 'var(--ok)' : 'var(--muted)' }}
                    title={`Previously ${trend.previous!.price.amount} on ${formatDate(trend.previous!.date)}`}
                  >
                    {change > 0 ? '▲' : change < 0 ? '▼' : '='} {Math.abs(change)}%
                  </span>
                ) : null}
              </span>
            </div>
            <div className="cd">
              {formatDate(trend.latest.date)} · {trend.latest.type} {trend.latest.ref}
              {trend.latest.customer ? ` · for ${trend.latest.customer}` : ''}
              {trend.previous ? ` · previously ${formatDate(trend.previous.date)}` : ' · first quote'}
            </div>
            {/*
              The supplier's own words about the offer — "Delivery 3 days · The above costing is
              based inches".

              On its own line and in the warning colour because it QUALIFIES the price above it: a
              rate three days out, or one quoted against a different unit, is not comparable with
              the others in this list, and nothing in the number says so. Absent for most rows,
              which renders nothing rather than an empty line.
            */}
            {trend.latest.remark ? (
              <div style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: 2 }}>
                “{trend.latest.remark}”
              </div>
            ) : null}
            {/*
              Take this supplier's past quote as the line's price.

              The rate is already known, so there is nothing for an RFQ to discover — the line is
              routed off RFQ at exactly what they quoted, and `contractQuotation.ts` carries it
              onto the customer quotation. No supplier quotation is created: they have not quoted
              THIS enquiry, and inventing a document saying they had would be a lie in the audit
              trail.
            */}
            {onPrice ? (
              <div style={{ marginTop: 4 }}>
                <Button
                  size="sm"
                  disabled={trend.latest.price.amount <= 0}
                  onClick={() => {
                    const patch = priceFromHistory(trend.latest)
                    if (patch) onPrice(patch, `${trend.supplier} at ${formatMoney(trend.latest.price)}`)
                  }}
                  title={`Price this line at ${formatMoney(trend.latest.price)} from ${trend.supplier} — no RFQ`}
                >
                  Use this price
                </Button>
                {/* The unit their rate was quoted against, when it differs from the line's own.
                    A rate per DOZ applied to a line counted in Nos is out by twelve. */}
                {trend.latest.uom && uom && trend.latest.uom !== uom ? (
                  <span style={{ color: 'var(--danger)', fontSize: 11, marginLeft: 8 }}>
                    ⚠ quoted per {trend.latest.uom}, this line is in {uom}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        )
      })}
    </Modal>
  )
}
